// Wire-format types and API contract for Health Encyclopedia (PRD 10 v2.1).
// Mirrors the error/envelope patterns used by `managementGuidelinesApi` so the
// adapter/mock split stays consistent across features. See the full contract:
// docs/api-contracts/health-encyclopedia.md.
//
// Key differences vs. Management Guidelines (PRD 07):
//   - Categories are an INDEPENDENT single-level master (not the PRO tree).
//   - The list is SERVER-SIDE (search / category filter / sort / pagination)
//     — see ContentListParams / ContentListResult.
//   - Delete (content + category) is Root-only (gated on isRoot, not useCan).

// PRD §4.5 — canonical status codes; UI maps to 게시중 / 임시저장.
export type ContentStatus = 'DRAFT' | 'PUBLISHED';

// PRD §3.9 (v2.4, 26.07.01) — the cap on how many PUBLISHED contents may be exposed
// on the patient-app home card is no longer a constant here. It is served by the API
// as ContentListResult.maxHomeExposed, read from one shared settings row, so the
// screen cannot disagree with the guard the server actually enforces. A hand-copied
// number is exactly what drifted: changing the server left this file saying "3".
// The value below is only the fallback used before the first list response arrives.
export const MAX_HOME_EXPOSED_FALLBACK = 3;

export const CONTENT_STATUSES: readonly ContentStatus[] = ['DRAFT', 'PUBLISHED'] as const;

// PRD §3.7 / §4.5 — block-editor block. Flat ordered array; array order is the
// render order (drag-and-drop reorder in Slice 6). Shape is intentionally
// structurally compatible with management-guidelines' ContentBlock so the
// block-editor components can be promoted to src/shared/components/block-editor
// in Slice 6 with minimal churn.
export type ContentBlockType = 'text' | 'image' | 'video';

export interface ContentBlock {
  // Client-stable id within one article (mock/edit session).
  id: number;
  type: ContentBlockType;
  // text: sanitized WYSIWYG HTML. image/video: the optional description shown
  // under the media, surfaced only when `showText` is true (PRD v2.5 §3.7).
  text: string;
  // image: uploaded URL. Mock phase carries a base64 data: URL (see §4).
  dataUrl?: string;
  // video: external URL (YouTube/Vimeo).
  url?: string;
  // image/video: optional title (PRD v2.5 §3.7 — block editor unified with
  // 관리지침/상담매뉴얼). Video additionally caps at 50 chars (ADM.018).
  title?: string;
  // image/video: "텍스트 설명 노출" toggle (PRD v2.5 §3.7). When false the
  // description (`text`) is authored-but-hidden in the patient app.
  showText?: boolean;
}

// PRD §4.1 — category master row. `contentCount` is server-computed and drives
// the filter dropdown counts + the delete guard.
export interface Category {
  id: number;
  name: string;
  order: number;
  contentCount: number;
}

// PRD §3.6 — category master list. Uncategorized (미분류) was removed from scope
// per clarification ADM.010 (category is required + in-use categories can't be
// deleted → no content can ever be uncategorized), so there is no bucket/count.
export interface CategoryListResult {
  categories: Category[];
}

// PRD §4.2 — list row. `categoryName` is server-resolved by id (rename reflects
// automatically); null when the content is uncategorized (미분류).
export interface ContentSummary {
  // 'E-###' (PRD §1.3 contract) — opaque, server-assigned. See contract §1.3.
  eid: string;
  title: string;
  categoryId: number | null;
  categoryName: string | null;
  thumbnailUrl: string | null;
  // HTML-stripped, server-truncated (~100 chars) per PRD §4.5.
  preview: string;
  status: ContentStatus;
  // 'YYYY.MM.DD' display string; null while DRAFT.
  publishedAt: string | null;
  // PRD §3.9 / §4.2 — patient-app home-card exposure. Only PUBLISHED contents can
  // be true; a PUBLISHED→DRAFT save clears it server-side.
  homeExposed: boolean;
}

// PRD §3.6 — detail returned when opening the form; adds the block array.
export interface ContentDetail extends ContentSummary {
  blocks: ContentBlock[];
}

// ── List query (server-side) ────────────────────────────────────────────────
export type ContentSortKey = 'eid' | 'title' | 'category' | 'publishedAt';
export type SortDir = 'asc' | 'desc';

// A category id selects that category; omit categoryId for "전체 카테고리".
// (미분류/UNCATEGORIZED removed from scope — clarification ADM.010.)
export type CategoryFilter = number;

export interface ContentListParams {
  page: number;
  size: number;
  sortKey: ContentSortKey;
  sortDir: SortDir;
  categoryId?: CategoryFilter;
  q?: string;
}

export interface ContentListResult {
  content: ContentSummary[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  // PRD §3.9 — home-exposed contents across the WHOLE library, deliberately
  // unaffected by page / categoryId / q. The banner count and the "3 reached →
  // disable the remaining toggles" rule are library-wide rules, and this list is
  // server-paginated at 20 rows, so counting the current page would under-count
  // whenever the ON contents sit on another page (or outside the active filter).
  homeExposedCount: number;
  // PRD §3.9 — the cap itself, served rather than hardcoded here, so the toggle-disable
  // rule and the banner text always match the limit the server enforces.
  maxHomeExposed: number;
}

// ── Mutations ────────────────────────────────────────────────────────────────
export interface CreateContentInput {
  title: string;
  categoryId: number;
  blocks: ContentBlock[];
  thumbnailUrl?: string | null;
  // DRAFT for 임시저장, PUBLISHED for 게시.
  status: ContentStatus;
}

// SHCT-896 — `clearThumbnail` lives on the UPDATE shape only: there is nothing to clear on a record
// that does not exist yet. It is needed because a null `thumbnailUrl` and an omitted one arrive at the
// server identically, so null is already spent on "leave unchanged" and cannot also mean "remove it".
// Same convention as the patient panel's `clearSubDiagnosis`. When true the server ignores
// `thumbnailUrl`: one request cannot both set and clear the same field.
export type UpdateContentInput = Partial<CreateContentInput> & {
  clearThumbnail?: boolean;
};

export interface CategoryNameInput {
  name: string;
}

export interface UploadResult {
  url: string;
}

// ── Error model ──────────────────────────────────────────────────────────────
// SHCT-712 — this list is the RUNTIME allowlist, not just a type. `mapError`
// narrows the server's `error.code` against it, so a code the FE does not know
// (an infrastructure 5xx like `INTERNAL`) can never carry the server's raw
// `message` into the UI. Kept as a const array so the type cannot drift from
// the check. Adding a code here means adding an `errors.{code}` i18n key too.
export const HEALTH_ENCYCLOPEDIA_API_ERROR_CODES = [
  'TITLE_REQUIRED',
  'TITLE_TOO_LONG',
  'CATEGORY_REQUIRED',
  'CONTENT_REQUIRED',
  // Block-editor rules — codes mirror the BE (BlockContentValidator):
  //   ADM.015 ≤10 blocks / ≤500 chars/text; ADM.016 no empty block;
  //   ADM.018 video URL = https + YouTube/Vimeo host, optional 1~50-char title.
  'TOO_MANY_BLOCKS',
  'TEXT_BLOCK_TOO_LONG',
  'EMPTY_BLOCK',
  'CONTENT_URL_NOT_ALLOWED',
  'VIDEO_TITLE_INVALID',
  // Image upload rejected by the BE magic-byte sniff (non JPG/PNG or > 5 MB).
  'CONTENT_FILE_INVALID',
  'CATEGORY_NAME_REQUIRED',
  'CATEGORY_NAME_TOO_LONG',
  'CATEGORY_NAME_DUPLICATE',
  // Wire code emitted by BE (ErrorCode.CATEGORY_IN_USE); was FE-only
  // 'CATEGORY_HAS_CONTENT' which never matched the server envelope.
  'CATEGORY_IN_USE',
  'STATUS_TRANSITION_FORBIDDEN',
  // PRD §3.9 home-exposure guards — all three emitted by the BE
  // (EncyclopediaController / EncyclopediaService, SHCT-486):
  //   HOME_EXPOSE_NOT_PUBLISHED (409) turning ON a DRAFT content;
  //   HOME_EXPOSE_LIMIT_EXCEEDED (409) a 4th ON attempt;
  //   HOME_EXPOSE_VALUE_REQUIRED (400) missing/none body flag.
  'HOME_EXPOSE_NOT_PUBLISHED',
  'HOME_EXPOSE_LIMIT_EXCEEDED',
  'HOME_EXPOSE_VALUE_REQUIRED',
  'NOT_FOUND',
  'INSUFFICIENT_PERMISSION',
  'UPLOAD_FAILED',
  'UNKNOWN',
] as const;

export type HealthEncyclopediaApiErrorCode =
  (typeof HEALTH_ENCYCLOPEDIA_API_ERROR_CODES)[number];

/** True only for a code the FE has i18n copy for; everything else is a System Error. */
export function isHealthEncyclopediaApiErrorCode(
  code: unknown,
): code is HealthEncyclopediaApiErrorCode {
  return (
    typeof code === 'string' &&
    (HEALTH_ENCYCLOPEDIA_API_ERROR_CODES as readonly string[]).includes(code)
  );
}

export class HealthEncyclopediaApiError extends Error {
  readonly code: HealthEncyclopediaApiErrorCode;
  readonly fields?: Record<string, string>;
  readonly meta?: Record<string, unknown>;

  constructor(
    code: HealthEncyclopediaApiErrorCode,
    message: string,
    options?: { fields?: Record<string, string>; meta?: Record<string, unknown> },
  ) {
    super(message);
    this.name = 'HealthEncyclopediaApiError';
    this.code = code;
    this.fields = options?.fields;
    this.meta = options?.meta;
    Object.setPrototypeOf(this, HealthEncyclopediaApiError.prototype);
  }
}

// ── API surface ──────────────────────────────────────────────────────────────
export interface HealthEncyclopediaApi {
  // PRD §3.1–3.3 — server-side list (filter/search/sort/paginate).
  listContents(params: ContentListParams): Promise<ContentListResult>;
  // PRD §3.6 — opens the edit form with blocks hydrated.
  getContent(eid: string): Promise<ContentDetail>;
  createContent(input: CreateContentInput): Promise<ContentDetail>;
  // PRD §3.6 — PUBLISHED → DRAFT is forbidden by BE.
  updateContent(eid: string, input: UpdateContentInput): Promise<ContentDetail>;
  // PRD §3.8 — Root only.
  deleteContent(eid: string): Promise<void>;
  // PRD §3.9 — flip the patient-home exposure flag. Needs CONTENT_LIB:WRITE;
  // the BE enforces "PUBLISHED only" and the max-3 cap. Returns the updated
  // content so the caller can reconcile without a second read.
  setHomeExpose(eid: string, homeExposed: boolean): Promise<ContentDetail>;

  // PRD §3.4 — category master + counts for the filter dropdown.
  listCategories(): Promise<CategoryListResult>;
  createCategory(input: CategoryNameInput): Promise<Category>;
  updateCategory(id: number, input: CategoryNameInput): Promise<Category>;
  // PRD §3.4 — blocked when contentCount > 0; Root only.
  deleteCategory(id: number): Promise<void>;

  // PRD §3.5 / §3.7 — thumbnail + image-block upload (feature-specific endpoint).
  uploadImage(file: File): Promise<UploadResult>;
}
